import React, { useRef, useState } from 'react';
import emailjs from '@emailjs/browser';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertProps } from '@mui/material/Alert';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import EmailIcon from '@mui/icons-material/Email';
import setup from '../assets/data/setup.json';
import MotionSection from './ui/MotionSection';
import SectionHeader from './ui/SectionHeader';
import ResumeDownloadButtons from './ResumeDownloadButtons';

const Alert = React.forwardRef<HTMLDivElement, AlertProps>(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

function Contact({ audience = 'engineering' }: { audience?: 'business' | 'engineering' } = {}) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [nameError, setNameError] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [messageError, setMessageError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

  const form = useRef<HTMLFormElement | null>(null);

  const sendEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    setNameError(name === '');
    setEmailError(email === '');
    setMessageError(message === '');

    if (name !== '' && email !== '' && message !== '') {
      setIsLoading(true);
      try {
        await emailjs.send(
          setup.emailJS_serviceID,
          setup.emailJS_templateID,
          { from_name: name, email, message },
          setup.emailJS_publicKey
        );
        setSnackbarMessage('Message sent successfully!');
        setSnackbarSeverity('success');
        setName('');
        setEmail('');
        setMessage('');
      } catch {
        setSnackbarMessage('Failed to send message. Please try again.');
        setSnackbarSeverity('error');
      } finally {
        setIsLoading(false);
        setOpenSnackbar(true);
      }
    }
  };

  const links = [
    { icon: <EmailIcon />, label: 'Email', href: `mailto:${setup.profile.email}`, external: false },
    { icon: <LinkedInIcon />, label: 'LinkedIn', href: setup.profile.linkedin, external: true },
    { icon: <GitHubIcon />, label: 'GitHub', href: setup.profile.github, external: true },
  ];

  const isBusiness = audience === 'business';

  return (
    <MotionSection id="contact">
      <SectionHeader
        eyebrow="Get in touch"
        title={
          isBusiness
            ? "Let's talk about the problem you're trying to solve."
            : "Let's build something ambitious together."
        }
        subtitle={
          isBusiness
            ? 'Discovery calls for custom software, automation, integrations, and systems that help your business run smoother.'
            : 'Senior engineering roles, contract work, and consulting on enterprise software, automation, and cloud architecture.'
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="space-y-6">
          <p className="text-ink-muted leading-relaxed">
            {isBusiness
              ? "Tell me what's broken, slow, or held together with spreadsheets. I'll help you figure out whether custom software is the right move—and what it would take."
              : "I'm available for senior engineering roles, contract work, and consulting. Tell me about the problem you're solving—I focus on outcomes, not just tech stacks."}
          </p>

          <div className="grid grid-cols-2 gap-3">
            {links.map((link) => (
              <a
                key={link.label}
                href={link.href}
                target={link.external ? '_blank' : undefined}
                rel="noopener noreferrer"
                className="ui-card flex items-center gap-3 p-4 text-ink-secondary hover:text-ink"
              >
                {link.icon}
                <span className="text-sm font-medium">{link.label}</span>
              </a>
            ))}
            <ResumeDownloadButtons variant="contact" />
          </div>

          <a
            href={setup.profile.calendly}
            target="_blank"
            rel="noopener noreferrer"
            className="ui-btn-primary"
          >
            <CalendarTodayIcon sx={{ fontSize: 18 }} />
            Schedule a Call on Calendly
          </a>
        </div>

        <form ref={form} onSubmit={sendEmail} className="ui-card p-5 sm:p-6 space-y-5">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-ink-secondary mb-1.5">
              Name
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              className={`w-full px-4 py-2.5 rounded-lg border bg-canvas text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent/30 ${nameError ? 'border-red-500' : 'border-line'}`}
            />
            {nameError && <p className="text-red-500 text-xs mt-1">Please enter your name</p>}
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-ink-secondary mb-1.5">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@company.com"
              className={`w-full px-4 py-2.5 rounded-lg border bg-canvas text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent/30 ${emailError ? 'border-red-500' : 'border-line'}`}
            />
            {emailError && <p className="text-red-500 text-xs mt-1">Please enter your email</p>}
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-ink-secondary mb-1.5">
              Message
            </label>
            <textarea
              id="message"
              rows={5}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Tell me about your project..."
              className={`w-full px-4 py-2.5 rounded-lg border bg-canvas text-ink placeholder:text-ink-muted focus:outline-none focus:ring-2 focus:ring-accent/30 resize-none ${messageError ? 'border-red-500' : 'border-line'}`}
            />
            {messageError && <p className="text-red-500 text-xs mt-1">Please enter a message</p>}
          </div>

          <button type="submit" disabled={isLoading} className="ui-btn-primary disabled:opacity-50">
            {isLoading ? 'Sending...' : 'Send Message'}
          </button>
        </form>
      </div>

      <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={() => setOpenSnackbar(false)}>
        <Alert onClose={() => setOpenSnackbar(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </MotionSection>
  );
}

export default Contact;
